import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FiX } from 'react-icons/fi';
import { adminTheme } from '../../theme-admin';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  z-index: ${adminTheme.zIndex.modal};
  animation: ${fadeIn} 0.2s ease-out;
  padding: ${adminTheme.spacing.lg};
`;

const Card = styled.div`
  background: ${adminTheme.colors.background.primary};
  border-radius: ${adminTheme.borderRadius['2xl']};
  width: min(1200px, 95%);
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${adminTheme.shadows['2xl']};
  animation: ${slideUp} 0.3s ease-out;
  border: 1px solid ${adminTheme.colors.gray[200]};
`;

const Header = styled.div`
  padding: ${adminTheme.spacing.xl} ${adminTheme.spacing.xl} ${adminTheme.spacing.lg};
  border-bottom: 1px solid ${adminTheme.colors.gray[200]};
  font-family: ${adminTheme.fonts.heading};
  font-weight: ${adminTheme.fontWeights.bold};
  font-size: ${adminTheme.fontSizes['2xl']};
  color: ${adminTheme.colors.gray[900]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${adminTheme.spacing.sm};
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: ${adminTheme.borderRadius.lg};
  border: none;
  background: transparent;
  color: ${adminTheme.colors.gray[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    color: ${adminTheme.colors.gray[700]};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Body = styled.div`
  padding: ${adminTheme.spacing.xl};
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${adminTheme.colors.gray[100]};
    border-radius: ${adminTheme.borderRadius.full};
  }

  &::-webkit-scrollbar-thumb {
    background: ${adminTheme.colors.gray[400]};
    border-radius: ${adminTheme.borderRadius.full};

    &:hover {
      background: ${adminTheme.colors.gray[500]};
    }
  }
`;

const Footer = styled.div`
  padding: ${adminTheme.spacing.lg} ${adminTheme.spacing.xl};
  border-top: 1px solid ${adminTheme.colors.gray[200]};
  background: ${adminTheme.colors.gray[50]};
  display: flex;
  justify-content: flex-end;
  gap: ${adminTheme.spacing.md};
`;

const Button = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.xl};
  border-radius: ${adminTheme.borderRadius.lg};
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[700]};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.base};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    border-color: ${adminTheme.colors.gray[400]};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Primary = styled(Button)`
  background: ${adminTheme.gradients.primary};
  color: white;
  border: none;
  box-shadow: ${adminTheme.shadows.md};

  &:hover {
    filter: brightness(1.1);
    box-shadow: ${adminTheme.shadows.lg};
  }
`;

export default function Modal({ open, title, children, onCancel, onSave, saveLabel = 'Save' }) {
  if (!open) return null;
  
  return (
    <Backdrop>
      <Card onClick={(e)=>e.stopPropagation()} role="dialog" aria-modal="true">
        <Header>
          <span>{title}</span>
          <CloseButton onClick={onCancel} aria-label="Close dialog">
            <FiX />
          </CloseButton>
        </Header>
        <Body>{children}</Body>
        <Footer>
          <Button onClick={onCancel}>Cancel</Button>
          <Primary onClick={onSave}>{saveLabel}</Primary>
        </Footer>
      </Card>
    </Backdrop>
  );
}
